import { Edit05 } from '@untitledui/icons/Edit05';
import { useState } from 'react';

import type { AvatarProps, AvatarSize } from '@/components/base/avatar/avatar';

import { Avatar } from '@/components/base/avatar/avatar';
import { Dialog, Modal, ModalOverlay } from '@/components/base/modals/modal';
import {
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from '@/components/base/tooltip/tooltip';
import { ProfilePictureUpload } from '@/components/profile-picture-upload/profile-picture-upload';
import { cx } from '@/utils/cx';

const iconSizes: Record<AvatarSize, string> = {
  xxs: 'size-3',
  xs: 'size-3',
  sm: 'size-4',
  md: 'size-4',
  lg: 'size-5',
  xl: 'size-6',
  '2xl': 'size-6',
};

interface AvatarEditButtonProps extends AvatarProps {
  userId: string;
  title?: string;
  onUploadSuccess?: () => void;
}

export const AvatarEditButton = ({
  userId,
  title = 'Change profile picture',
  className,
  size = 'md',
  onUploadSuccess,
  ...avatarProps
}: AvatarEditButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <AriaTooltip title={title}>
        <AriaTooltipTrigger
          aria-label={title}
          onPress={() => setIsModalOpen(true)}
          className={cx(
            'group/avatar-edit relative flex shrink-0 cursor-pointer rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2',
            className,
          )}
        >
          <Avatar size={size} {...avatarProps} />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity duration-150 group-hover/avatar-edit:opacity-100 group-focus-visible/avatar-edit:opacity-100"
          >
            <Edit05 className={cx('text-white', iconSizes[size])} />
          </span>
        </AriaTooltipTrigger>
      </AriaTooltip>

      <ModalOverlay centered isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal>
          <Dialog>
            <div className="w-full max-w-xl rounded-xl bg-primary p-6 shadow-xl">
              <ProfilePictureUpload
                userId={userId}
                onUploadSuccess={() => {
                  setIsModalOpen(false);
                  onUploadSuccess?.();
                }}
                onCancel={() => setIsModalOpen(false)}
              />
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};
